/**
 * Choosing which part of a picture fills its frame. §6.4.
 *
 * The frame does not move. The picture does — dragged behind a fixed opening,
 * with the rest of it shown faintly around the edges so it is obvious what is
 * being given up. That is the gesture every phone gallery uses, and it is the
 * one a customer already knows.
 *
 * The alternative, handles on a crop rectangle, was rejected for a specific
 * reason: the frame here is a printed object with a size the customer chose in
 * millimetres. A crop rectangle that can be dragged to any shape would either
 * change that size or squash the picture into it, and both are wrong. Locking
 * the visible region to the frame's own proportions makes distortion
 * impossible rather than merely discouraged.
 *
 * So the whole gesture is two numbers: how much of the picture shows, and
 * where. Everything below is those two, kept inside their limits.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Group, Image as KonvaImage, Line, Rect } from 'react-konva';
import type Konva from 'konva';
import { useEditor } from '../state/store';
import { mmToPx } from '../model/units';
import type { ImageLayer, Layer } from '../model/types';
import { imageFor, onAssetsChanged } from '../assets';
import { frameOf } from './frame';
import { sourceRegion, wholeImage } from '../press/draw';

const ACCENT = '#dd1d26';

/**
 * The least of a picture that may be shown.
 *
 * Not a quality judgement — preflight already says when a crop has pushed the
 * resolution too low, and says it in dots per inch, which is the number that
 * actually decides. This only stops the arithmetic reaching zero.
 */
const MIN_SHOWN = 0.02;

/** How much one notch of the wheel changes it. */
const WHEEL_STEP = 0.08;

export function CropBox({ zoom }: { zoom: number }) {
  const cropping = useEditor((s) => s.cropping);
  const preview = useEditor((s) => s.view.preview);
  const page = useEditor((s) => s.page());
  const [, redraw] = useState(0);

  /*
   * A picture arriving late has to reach this overlay too.
   *
   * Without it, choosing Crop before the bitmap has decoded - which is most of
   * a second on a phone, and the whole of it on a slow connection - left the
   * frame with nothing in it and no way to get it back short of clicking off
   * the picture and on again. ImageNode has carried this subscription since it
   * was written; this one needs it for the same reason.
   */
  useEffect(() => onAssetsChanged(() => redraw((n) => n + 1)), []);

  if (preview || !cropping || !page) return null;

  const layer = find(page.layers, cropping);

  if (!layer || layer.type !== 'image' || !layer.asset) return null;

  const picture = imageFor(layer.asset);

  if (!picture) return null;

  return <Frame layer={layer} picture={picture} zoom={zoom} />;
}

function Frame({
  layer,
  picture,
  zoom,
}: {
  layer: ImageLayer;
  picture: HTMLImageElement;
  zoom: number;
}) {
  const grab = useRef<Konva.Rect>(null);

  const source = { w: picture.naturalWidth, h: picture.naturalHeight };
  const ratio = shownRatio(layer, source);

  const w = mmToPx(layer.w, zoom);
  const h = mmToPx(layer.h, zoom);

  /*
   * Entering the mode settles the picture into a shape the gesture can work
   * with: the visible region takes the frame's proportions, and the fit is set
   * to fill because the region now *is* the fit. Whatever was on screen a
   * moment ago is kept as closely as those limits allow, so the picture does
   * not jump when the handles appear.
   */
  useEffect(() => {
    const settled = settle(sourceRegion(layer, source) ?? layer.crop, ratio);

    if (same(settled, layer.crop) && layer.fit === 'fill') return;

    useEditor.getState().updateLayer(layer.id, 'Crop', (l) =>
      l.type === 'image' ? { ...l, crop: settled, fit: 'fill' } : l
    );
    // Once, on the way in. Re-running it as the crop changes would fight the
    // customer's own dragging.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer.id]);

  const whole = wholeImage({ x: 0, y: 0, w, h }, layer.crop);

  const setCrop = useCallback(
    (crop: ImageLayer['crop']) => {
      useEditor.getState().updateLayer(
        layer.id,
        'Crop',
        (l) => (l.type === 'image' ? { ...l, crop: settle(crop, ratio) } : l),
        // The whole gesture is one undo step, like a drag or a resize.
        { coalesce: `crop:${layer.id}` }
      );
    },
    [layer.id, ratio]
  );

  /** Dragging moves the picture, so the crop moves the opposite way. */
  const onDragMove = useCallback(() => {
    const node = grab.current;

    if (!node) return;

    setCrop({
      ...layer.crop,
      x: layer.crop.x - node.x() / whole.w,
      y: layer.crop.y - node.y() / whole.h,
    });

    // The picture is redrawn from the new crop, so the pad must go back to
    // where it started or the two would chase each other.
    node.position({ x: 0, y: 0 });
  }, [layer.crop, setCrop, whole.w, whole.h]);

  /** The wheel scales about the middle of the frame, which is what is looked at. */
  const onWheel = useCallback(
    (event: Konva.KonvaEventObject<WheelEvent>) => {
      event.evt.preventDefault();

      const step = event.evt.deltaY > 0 ? 1 + WHEEL_STEP : 1 - WHEEL_STEP;
      const shown = layer.crop.h * step;

      setCrop({
        // Growing the region by d means moving its corner back by half of d,
        // so the middle stays where it is.
        x: layer.crop.x - ( ratio * shown - layer.crop.w ) / 2,
        y: layer.crop.y - ( shown - layer.crop.h ) / 2,
        w: ratio * shown,
        h: shown,
      });
    },
    [layer.crop, ratio, setCrop]
  );

  return (
    /*
     * Named for the picture it belongs to.
     *
     * The canvas works out what was clicked by walking up to the nearest named
     * node, and anything unnamed reads as bare paper - which clears the
     * selection. The crop frame sits directly over its own picture, so without
     * a name a click inside it cancelled the crop the customer was in the
     * middle of making.
     */
    <Group name={layer.id} {...frameOf(layer, zoom)}>
      {/* The part being given up, still there so the customer can see what
          they are choosing between. */}
      <KonvaImage
        image={picture}
        x={whole.x}
        y={whole.y}
        width={whole.w}
        height={whole.h}
        opacity={0.28}
        listening={false}
      />

      {/* And the part that prints, at full strength inside the opening. */}
      <Group clipX={0} clipY={0} clipWidth={w} clipHeight={h} listening={false}>
        <KonvaImage image={picture} x={whole.x} y={whole.y} width={whole.w} height={whole.h} />
      </Group>

      <Rect
        width={w}
        height={h}
        stroke={ACCENT}
        strokeWidth={2}
        strokeScaleEnabled={false}
        listening={false}
      />

      <Thirds w={w} h={h} />

      {/*
        A transparent pad over the opening takes the drag. Konva has no drag
        without a node to move, so this one moves and is put straight back,
        with its travel read off as the gesture.
      */}
      <Rect
        ref={grab}
        width={w}
        height={h}
        draggable
        onDragMove={onDragMove}
        onWheel={onWheel}
        onMouseEnter={(event) => {
          const stage = event.target.getStage();

          if (stage) stage.container().style.cursor = 'move';
        }}
        onMouseLeave={(event) => {
          const stage = event.target.getStage();

          if (stage) stage.container().style.cursor = '';
        }}
      />
    </Group>
  );
}

/** Thirds, faint. The oldest framing aid there is, and it costs four lines. */
function Thirds({ w, h }: { w: number; h: number }) {
  const lines = [
    [w / 3, 0, w / 3, h],
    [(w * 2) / 3, 0, (w * 2) / 3, h],
    [0, h / 3, w, h / 3],
    [0, (h * 2) / 3, w, (h * 2) / 3],
  ];

  return (
    <>
      {lines.map((points, index) => (
        <Line
          key={index}
          points={points}
          stroke="#ffffff"
          strokeWidth={1}
          strokeScaleEnabled={false}
          opacity={0.45}
          listening={false}
        />
      ))}
    </>
  );
}

/* ---------------------------------------------------------------
 * The two numbers, and their limits
 * ------------------------------------------------------------ */

/**
 * How wide the shown region must be for every unit of its height.
 *
 * The region is measured in proportions of the source, so a square region of a
 * picture twice as wide as it is tall is 0.5 by 1 - which is why the source's
 * own shape appears here as well as the frame's.
 */
export function shownRatio( layer: Layer, source: { w: number; h: number } ): number {
  if ( layer.h <= 0 || source.w <= 0 || source.h <= 0 ) return 1;

  return ( layer.w / layer.h ) * ( source.h / source.w );
}

/**
 * The nearest crop that is legal: right proportions, inside the picture.
 *
 * Every path into the crop goes through here, so there is no route by which a
 * squashed or out-of-bounds region can reach the document - including the one
 * that matters most, a file from an older version of the editor.
 */
export function settle(
  crop: ImageLayer['crop'],
  ratio: number
): ImageLayer['crop'] {
  // Height leads and width follows, because height is the one the wheel and
  // the slider change.
  const most = Math.min( 1, ratio > 0 ? 1 / ratio : 1 );
  const h = clamp( crop.h, Math.min( MIN_SHOWN, most ), most );
  const w = Math.min( 1, ratio * h );

  return {
    x: round( clamp( crop.x, 0, Math.max( 0, 1 - w ) ) ),
    y: round( clamp( crop.y, 0, Math.max( 0, 1 - h ) ) ),
    w: round( w ),
    h: round( h ),
  };
}

const clamp = ( value: number, low: number, high: number ) =>
  Number.isFinite( value ) ? Math.min( high, Math.max( low, value ) ) : low;

const round = ( value: number ) => Math.round( value * 10000 ) / 10000;

const same = ( a: ImageLayer['crop'], b: ImageLayer['crop'] ) =>
  round( a.x ) === round( b.x ) && round( a.y ) === round( b.y )
  && round( a.w ) === round( b.w ) && round( a.h ) === round( b.h );

/** The layer, wherever it sits, including inside a group. */
function find( layers: Layer[], id: string ): Layer | null {
  for ( const layer of layers ) {
    if ( layer.id === id ) return layer;

    if ( layer.type === 'group' ) {
      const found = find( layer.children, id );

      if ( found ) return found;
    }
  }

  return null;
}
